import { useAccountQuota, getSubscriptionTierName } from '@/hooks/use-account-quota';
import { Progress } from '@/components/ui/progress';
import { Loader2, Zap, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/router';

interface AccountQuotaIndicatorProps {
  className?: string;
  showUpgradeButton?: boolean;
  compact?: boolean;
  refreshInterval?: number;
}

export function AccountQuotaIndicator({
  className = '',
  showUpgradeButton = true,
  compact = false,
  refreshInterval
}: AccountQuotaIndicatorProps) {
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const lastManualRefreshTime = useRef<number>(0);
  const { session } = useAuth();
  const [localQuotaInfo, setLocalQuotaInfo] = useState<{
    quota: number;
    currentCount: number;
    remainingSlots: number;
    canAddMore: boolean;
    subscriptionTier: string;
    lockedAccountsCount?: number;
  } | null>(null);
  const [isLocalLoading, setIsLocalLoading] = useState<boolean>(false);
  
  // 쿼리 설정에서 staleTime을 5분으로 변경하여 자주 리프레시되지 않도록 설정
  const { data: quotaInfo, isLoading: isQueryLoading, error, refetch } = useAccountQuota();
  const navigate = useNavigate();
  
  // 로컬 데이터 가져오기 함수
  const fetchLocalQuota = async () => {
    if (!session?.user?.id) return;
    
    setIsLocalLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_chat_credits')
        .select('account_quota')
        .eq('user_id', session.user.id)
        .single();
        
      if (error) {
        console.error('계정 쿼터 정보 조회 오류:', error);
        return;
      }
      
      // 현재 연결된 계정 수 확인
      const { count, error: countError } = await supabase
        .from('saved_instagram_accounts')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)
        .is('deleted_at', null);
        
      if (countError) {
        console.error('계정 수 조회 오류:', countError);
        return;
      }
      
      const quota = data?.account_quota ?? 2;
      const currentCount = count || 0;
      const remainingSlots = quota === -1 ? -1 : Math.max(0, quota - currentCount);
      
      // 구독 등급 결정
      let subscriptionTier = 'free';
      if (quota === -1) {
        subscriptionTier = 'premium';
      } else if (quota === 10) {
        subscriptionTier = 'basic';
      }
      
      // 잠긴 계정 수 계산
      const lockedAccountsCount = quota === -1 ? 0 : Math.max(0, currentCount - quota);
      
      setLocalQuotaInfo({
        quota,
        currentCount,
        remainingSlots,
        canAddMore: quota === -1 || currentCount < quota,
        subscriptionTier,
        lockedAccountsCount
      });
    } catch (err) {
      console.error('계정 쿼터 정보 조회 중 예외 발생:', err);
    } finally {
      setIsLocalLoading(false);
    }
  };
  
  // 초기 로드
  useEffect(() => {
    fetchLocalQuota();
  }, [session?.user?.id]);
  
  // 수동 새로고침 함수 (디바운싱 적용)
  const handleManualRefresh = () => {
    const now = Date.now();
    // 마지막 새로고침 후 최소 10초가 지나야 다시 새로고침 가능
    if (now - lastManualRefreshTime.current > 10000) {
      refetch();
      fetchLocalQuota();
      lastManualRefreshTime.current = now;
    }
  };
  
  // 주기적 새로고침 설정 (선택적) - 디바운싱 적용
  useEffect(() => {
    if (!refreshInterval) return;
    
    const intervalId = setInterval(() => {
      const now = Date.now();
      // 마지막 새로고침 후 refreshInterval 이상 지났을 때만 새로고침
      if (now - lastFetchTime > refreshInterval) {
        refetch();
        fetchLocalQuota();
        setLastFetchTime(now);
      }
    }, refreshInterval);
    
    return () => clearInterval(intervalId);
  }, [refreshInterval, refetch, lastFetchTime]);
  
  // 초기 로딩 시 lastFetchTime 설정
  useEffect(() => {
    if ((!isQueryLoading && quotaInfo && lastFetchTime === 0) || 
        (!isLocalLoading && localQuotaInfo && lastFetchTime === 0)) {
      setLastFetchTime(Date.now());
    }
  }, [isQueryLoading, quotaInfo, lastFetchTime, isLocalLoading, localQuotaInfo]);
  
  // 최종 데이터 결정 (API와 로컬 둘 다 고려)
  const finalQuotaInfo = quotaInfo || (localQuotaInfo ? {
    can_add: localQuotaInfo.canAddMore,
    current_count: localQuotaInfo.currentCount,
    quota: localQuotaInfo.quota,
    remaining: localQuotaInfo.remainingSlots,
    subscription_tier: localQuotaInfo.subscriptionTier,
    displayed_count: localQuotaInfo.currentCount,
    locked_count: localQuotaInfo.lockedAccountsCount || 0
  } : null);
  
  const isLoading = isQueryLoading && isLocalLoading;

  // 로딩 중이거나 오류 상태일 때 표시할 내용
  if (isLoading) {
    return (
      <div className={`flex items-center justify-between rounded-lg border p-4 ${className}`}>
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm">계정 정보 로딩 중...</span>
        </div>
      </div>
    );
  }

  if (error || !finalQuotaInfo) {
    return (
      <div className={`flex items-center justify-between rounded-lg border p-4 ${className}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">계정 정보를 불러올 수 없습니다</span>
          <Button variant="outline" size="sm" onClick={handleManualRefresh}>
            다시 시도
          </Button>
        </div>
      </div>
    );
  }

  // 퍼센트 계산 (무제한이면 항상 0%)
  const isUnlimited = finalQuotaInfo.quota === -1;
  const usagePercent = isUnlimited ? 0 : (finalQuotaInfo.current_count / finalQuotaInfo.quota) * 100;
  
  // 상태에 따른 색상
  const getStatusColor = () => {
    if (isUnlimited) return 'text-green-500';
    if (usagePercent >= 90) return 'text-red-500';
    if (usagePercent >= 75) return 'text-orange-500';
    return 'text-primary';
  };

  const tierName = getSubscriptionTierName(finalQuotaInfo.subscription_tier);
  const statusColor = getStatusColor();
  const lockedCount = finalQuotaInfo.locked_count || Math.max(0, finalQuotaInfo.current_count - finalQuotaInfo.quota);

  // 컴팩트 모드 (간소화된 표시)
  if (compact) {
    return (
      <div 
        className={`rounded-lg border p-3 ${className} cursor-pointer hover:bg-muted/50 transition-colors`}
        onClick={() => navigate('/chat')}
        title="계정 관리를 위해 채팅 탭으로 이동"
      >
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-xs text-muted-foreground">계정 사용량</span>
          <span className={`text-xs font-medium ${statusColor}`}>
            {isUnlimited ? (
              '무제한'
            ) : (
              `${finalQuotaInfo.current_count} / ${finalQuotaInfo.quota}`
            )}
          </span>
        </div>
        {!isUnlimited && (
          <Progress value={usagePercent} className="h-1.5" />
        )}
      </div>
    );
  }

  // 일반 모드 (상세 표시)
  return (
    <div className={`rounded-lg border p-4 ${className}`}>
      <div 
        className="flex items-center justify-between mb-1 cursor-pointer hover:bg-muted/10 p-1 rounded transition-colors"
        onClick={() => navigate('/chat')}
        title="채팅 탭으로 이동하여 모든 계정 보기"
      >
        <div className="space-y-0.5">
          <h3 className="text-sm font-medium">계정 연결 제한</h3>
          <p className="text-xs text-muted-foreground">{tierName}</p>
        </div>
        <span className={`text-sm font-medium ${statusColor} flex items-center gap-1`}>
          {isUnlimited ? (
            <>
              <span>무제한</span>
            </>
          ) : (
            <>
              <span>{finalQuotaInfo.current_count} / {finalQuotaInfo.quota}</span>
              {finalQuotaInfo.current_count > finalQuotaInfo.quota && (
                <Lock className="h-3.5 w-3.5" />
              )}
            </>
          )}
        </span>
      </div>
      
      {!isUnlimited && (
        <>
          <Progress value={usagePercent} className="h-1.5 mb-3" />
          
          {/* 잠긴 계정이 있는 경우 알림 표시 */}
          {lockedCount > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 mb-2 p-2 bg-amber-50/50 dark:bg-amber-900/20 rounded flex items-center justify-between">
              <span className="flex items-center">
                <Lock className="h-3.5 w-3.5 mr-1.5" />
                <span>{lockedCount}개 계정이 쿼터 초과로 잠겨 있습니다</span>
              </span>
              
              {showUpgradeButton && (
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs text-primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate('/pricing');
                  }}
                >
                  업그레이드
                </Button>
              )}
            </div>
          )}
          
          {/* 숨겨진 계정이 있는 경우 알림 표시 */}
          {finalQuotaInfo.current_count > 0 && 
           finalQuotaInfo.quota > 0 && 
           finalQuotaInfo.displayed_count !== undefined && 
           finalQuotaInfo.current_count !== finalQuotaInfo.displayed_count && (
            <div className="text-xs text-muted-foreground mt-1 mb-2 p-2 bg-muted/50 rounded flex items-center justify-between">
              <span>
                {finalQuotaInfo.current_count - (finalQuotaInfo.displayed_count || 0)}개 계정이 표시되지 않고 있습니다
              </span>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/chat');
                }}
              >
                모두 보기
              </Button>
            </div>
          )}
        </>
      )}
      
      {showUpgradeButton && finalQuotaInfo.subscription_tier !== 'premium' && (
        <Button 
          size="sm" 
          onClick={() => navigate("/pricing")} 
          className="w-full mt-2 gradient-button"
        >
          <Zap className="h-3.5 w-3.5 mr-1.5" />
          {finalQuotaInfo.subscription_tier === 'free' ? '가입하고 더 많은 계정 연결하기' : '업그레이드'}
        </Button>
      )}
    </div>
  );
}